import { useState, type ReactNode } from 'react'
import { ExternalLink, Maximize2, Minimize2, X } from 'lucide-react'
import { cn } from '@/lib/cn'

/**
 * The shared right-side detail drawer used by the deal, contact and list cards. Collapsed it is a
 * fixed-width side panel; expanded it becomes a centered, wide full-screen panel. The header carries
 * a caller-supplied title slot plus the expand / close controls; `children` is a render prop given
 * the current `expanded` flag so the body can switch its layout (e.g. the deal's three-column view).
 */
export default function DetailDrawer({
  title,
  onClose,
  width = 'w-96',
  defaultExpanded = false,
  openInNewTabHref,
  children,
}: {
  /** Header content (heading, badges, subtitle) — laid out to the left of the controls. */
  title: ReactNode
  onClose: () => void
  /** Collapsed side-panel width (expanded always uses the wide centred panel). */
  width?: string
  /** Open already expanded (e.g. arrived via a deep-link / new-tab full-view URL). */
  defaultExpanded?: boolean
  /** When set, show an "open in a new tab" control that points at the record's own URL. */
  openInNewTabHref?: string
  children: (expanded: boolean) => ReactNode
}) {
  const [expanded, setExpanded] = useState(defaultExpanded)

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex',
        expanded ? 'items-center justify-center p-2 sm:p-4' : 'justify-end',
      )}
    >
      <div
        className={cn('absolute inset-0', expanded ? 'bg-black/60' : 'bg-black/30')}
        onClick={onClose}
      />
      <div
        className={cn(
          'relative bg-white shadow-2xl flex flex-col',
          expanded ? 'w-full h-full rounded-xl overflow-hidden' : cn(width, 'h-full'),
        )}
      >
        <div className="flex items-start justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2 min-w-0">{title}</div>
          <div className="flex items-center gap-3 shrink-0">
            {openInNewTabHref && (
              <a
                href={openInNewTabHref}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-600"
                title="Open in a new tab"
              >
                <ExternalLink size={16} />
              </a>
            )}
            <button
              onClick={() => setExpanded((v) => !v)}
              className="text-gray-400 hover:text-gray-600"
              title={expanded ? 'Collapse to side panel' : 'Expand to full screen'}
            >
              {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={18} />
            </button>
          </div>
        </div>

        {children(expanded)}
      </div>
    </div>
  )
}
